// AdminTeamMappingPage — composition root for the /admin/team-mapping
// surface (PRODOPSAI-508). Owns the single fetch to
// `/admin/team-memberships/tree` and renders three slots:
//
//   1. Filter bar  (search, region, archived toggle, Add Mapping)
//   2. UnmappedStrip   (US9 — PRODOPSAI-512)
//   3. TeamRoleTree    (US6 — PRODOPSAI-509)
//
// Filter state lives in URL search params so refresh / share-link works.
//
// CRUD wiring (PRODOPSAI-510 / 511):
//   - Header `[+ Add mapping]` opens AddMappingDialog with empty prefill.
//   - UnmappedStrip's "Map to team →" opens the dialog prefilled to that
//     Person and locks the Person picker.
//   - PersonCard's `[⋯]` opens EditMembershipPopover anchored to the button.
//   - On any successful save / change / delete the tree is refetched in one
//     round-trip and a status banner surfaces the result inline (no global
//     toast widget exists yet in this codebase — see CLAUDE.md note).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ITeamMembershipTreeResponse } from '@skystone/shared';
import { Button, Card, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { inputClass } from '@/lib/form-styles';
import { cn, getErrorMessage } from '@/lib/utils';
import { teamMembershipApi } from '@/services/team-membership-api';
import { AddMappingDialog } from '@/components/admin/AddMappingDialog';
import { TeamRoleTree } from '@/components/admin/TeamRoleTree';
import { UnmappedStrip } from '@/components/admin/UnmappedStrip';
import { listAvailableRegions } from '@/components/admin/team-role-tree-helpers';

function isEmpty(response: ITeamMembershipTreeResponse | null): boolean {
  if (response === null) return false;
  return response.teams.length === 0 && response.unmapped.length === 0;
}

export function AdminTeamMappingPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-backed filter state.
  const search = searchParams.get('q') ?? '';
  const region = searchParams.get('region') ?? '';
  const showArchived = searchParams.get('archived') === 'true';

  const [data, setData] = useState<ITeamMembershipTreeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // CRUD-flow state (US7 + US8; PRODOPSAI-539 per-team add).
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState<{ personId?: string; teamId?: string } | null>(
    null,
  );
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  // Sequence guard: rapid toggling of `archived` can interleave fetches, and
  // an older response arriving last would silently overwrite the newer one.
  // Each `load()` claims an id from this ref; only the latest id is allowed
  // to publish state (Rabizard #662 finding 3).
  const requestIdRef = useRef(0);

  const load = useCallback(async (includeArchived: boolean): Promise<void> => {
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await teamMembershipApi.getTeamMembershipTree({
        includeArchivedTeams: includeArchived,
      });
      if (id !== requestIdRef.current) return;
      setData(response);
    } catch (err: unknown) {
      if (id !== requestIdRef.current) return;
      setError(getErrorMessage(err, 'Failed to load team mappings'));
      // Re-throw so callers awaiting `load()` (e.g. post-mutation refetch)
      // can suppress a contradictory success banner when the refetch fails.
      throw err;
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // `load()` rejects on fetch failure so post-mutation refetches can
    // suppress a contradictory success banner — but in this effect the
    // ErrorBanner is already wired up via setError, so we silently swallow
    // here. An unhandled rejection inside a `useEffect` would otherwise
    // print a noisy warning to the console.
    load(showArchived).catch(() => {});
  }, [load, showArchived]);

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleSearchChange = (value: string) => updateParam('q', value);
  const handleRegionChange = (value: string) => updateParam('region', value);
  const handleArchivedToggle = () => {
    updateParam('archived', showArchived ? '' : 'true');
  };

  const handleAddMappingClick = () => {
    setAddPrefill(null);
    setStatusMessage(null);
    setAddDialogOpen(true);
  };

  const handleMapPerson = useCallback((personId: string) => {
    setAddPrefill({ personId });
    setStatusMessage(null);
    setAddDialogOpen(true);
  }, []);

  const handleAddToTeam = useCallback((teamId: string) => {
    setAddPrefill({ teamId });
    setStatusMessage(null);
    setAddDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setAddDialogOpen(false);
  }, []);

  // Rabizard #663 finding 3: success banner waits for the post-mutation
  // refetch to resolve. If `load()` rejects, the page already shows an
  // ErrorBanner (via setError inside load) — overlaying a green "Mapped X
  // to Y" banner on top would be contradictory.
  const handleMappingSaved = useCallback(
    async (info: { displayName: string; teamName: string; roleName: string }) => {
      try {
        await load(showArchived);
        setStatusMessage(
          `Mapped ${info.displayName} to ${info.teamName} as ${info.roleName}`,
        );
      } catch {
        // load() already populated the error banner; do not overwrite with a
        // success message.
      }
    },
    [load, showArchived],
  );

  const handleMembershipChanged = useCallback(
    async (message: string) => {
      try {
        await load(showArchived);
        setStatusMessage(message);
      } catch {
        // see handleMappingSaved
      }
    },
    [load, showArchived],
  );

  const availableRegions = useMemo(
    () => listAvailableRegions(data?.teams ?? null, showArchived),
    [data, showArchived],
  );
  const regionFilterValue = region.trim() === '' ? null : region;

  if (loading && data === null) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Team mapping"
        subtitle="Map people from Microsoft Entra to teams and assign their role."
        action={
          <Button onClick={handleAddMappingClick} data-testid="add-mapping-button">
            + Add mapping
          </Button>
        }
      />

      {error !== null && (
        <div className="space-y-2">
          <ErrorBanner message={error} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              load(showArchived).catch(() => {});
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {statusMessage !== null && (
        <output
          className={cn(
            'block rounded-md border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-700',
          )}
          data-testid="team-mapping-status"
        >
          {statusMessage}
        </output>
      )}

      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="flex-1">
          <label htmlFor="team-mapping-search" className="sr-only">
            Search people
          </label>
          <input
            id="team-mapping-search"
            type="search"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search people…"
            className={inputClass}
            data-testid="team-mapping-search"
          />
        </div>
        <div className="sm:w-56">
          <label htmlFor="team-mapping-region" className="sr-only">
            Region
          </label>
          <select
            id="team-mapping-region"
            value={region}
            onChange={(e) => handleRegionChange(e.target.value)}
            className={inputClass}
            data-testid="team-mapping-region"
          >
            <option value="">All regions</option>
            {availableRegions.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleArchivedToggle}
          aria-pressed={showArchived}
          data-testid="team-mapping-archived-toggle"
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </Card>

      {data !== null && isEmpty(data) === true && (
        <Card className="p-8 text-center" data-testid="team-mapping-empty">
          <h2 className="text-lg font-semibold">No mappings yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            No teams or unmapped people are visible. Use{' '}
            <span className="font-medium">+ Add mapping</span> to start.
          </p>
        </Card>
      )}

      {data !== null && isEmpty(data) === false && (
        <>
          <div data-testid="unmapped-strip-slot">
            <UnmappedStrip
              persons={data.unmapped}
              onMapPerson={handleMapPerson}
              searchQuery={search}
            />
          </div>

          <div data-testid="team-role-tree-slot">
            <TeamRoleTree
              teams={data.teams}
              searchQuery={search}
              regionFilter={regionFilterValue}
              showArchived={showArchived}
              onMembershipChanged={handleMembershipChanged}
              onAddToTeam={handleAddToTeam}
            />
          </div>
        </>
      )}

      <AddMappingDialog
        open={addDialogOpen}
        onClose={handleDialogClose}
        onSaved={handleMappingSaved}
        prefill={addPrefill}
      />
    </PageContainer>
  );
}
